import React, { useContext, useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import {
  Box,
  Card,
  CardActionArea,
  CardMedia,
  CardContent,
  Typography,
  LinearProgress,
} from '@mui/material';
import { useRouter } from 'next/router';
import {
  getOrCreateCanvas,
  renderDesignView,
  preloadImage
} from '@glowforge/builder-components/lib/util/image-helpers';
import {
  MagicSvgContext, MagicSvgDispatchContext,
} from '../../context/MagicSvgContext/MagicSvgContext';
import { magicSvgActions, PAGES, STATUS } from '../../context/MagicSvgContext/magicSvgReducer';
import MagicSvgShare from './magic-svg-share';
import { MagicSvgImageShape } from '../../context/MagicSvgContext/MagicSvgProvider';

const BLANK_IMAGE = 'data:image/gif;base64,R0lGODlhAQABAAAAACH5BAEKAAEALAAAAAABAAEAAAICTAEAOw==';

export default function ImageCard({
  magicPreviewMode,
  materialImageUrl,
  materialNominalThickness,
  clickable,
  description,
  imageData,
  width,
  height,
  showShare,
}) {
  const { generationStatus, image: currentImage } = useContext(MagicSvgContext);
  const { dispatch } = useContext(MagicSvgDispatchContext);
  const [preview, setPreview] = useState(null);
  const router = useRouter();

  const handleImageClick = (image) => {
    dispatch({
      type: magicSvgActions.SET_IMAGE,
      image,
    });

    dispatch({
      type: magicSvgActions.SET_GENERATION_STATUS,
      status: STATUS.SUCCESS,
    });
    router.push({
      query: {
        ...router.query,
        page: PAGES.RESULT,
        result: image.id,
        shallow: true,
      }
    });
  };

  useEffect(() => {
    async function generatePreview() {
      try {
        const getCanvas = getOrCreateCanvas();
        const artworkImage = await preloadImage(currentImage.svgUrl, true);
        const materialImage = await preloadImage(materialImageUrl, true);
        const options = { shadowDepth: materialNominalThickness };
        const magicPreviewImage = renderDesignView(artworkImage, materialImage, getCanvas, options);
        setPreview(magicPreviewImage);
      } catch (e) {
        console.error(`Failed to generate preview: ${e}`);
      }
    }

    if (currentImage.svgUrl && magicPreviewMode && generationStatus === STATUS.SUCCESS) {
      generatePreview();
    } else {
      setPreview(null);
    }
  }, [
    currentImage.svgUrl,
    generationStatus,
    magicPreviewMode,
    materialImageUrl,
    materialNominalThickness
  ]);

  const handleShareClick = (event) => {
    event.stopPropagation();
  };

  const cardContent = (
    <>
      {showShare && (
        <Box
          onMouseDown={handleShareClick}
          onClick={handleShareClick}
          sx={{
            position: 'absolute',
            top: '1px',
            right: '1px',
            zIndex: 1,
            '& svg': {
              stroke: 'none',
            }
          }}>
          <MagicSvgShare
            hideCopy
            hideDownload
            image={imageData}
            trackPrefix='recent-design'
          />
        </Box>
      )}
      <CardMedia
        component='img'
        src={(magicPreviewMode ? preview : imageData?.svgUrl) || BLANK_IMAGE}
        alt={(magicPreviewMode ? currentImage.prompt : imageData?.prompt) || 'blank'}
        sx={{
          width,
          height,
          objectFit: 'contain',
        }}
      />
      {!(preview || imageData?.svgUrl) && <LinearProgress color='secondary' />}
      {description && (
        <CardContent sx={{ background: '#FDF5E9' }}>
          <Typography>{description}</Typography>
        </CardContent>
      )}
    </>
  );

  // TODO: Add social share button on the card media
  return (
    <Card>
      {clickable ? (
        <CardActionArea onClick={() => handleImageClick({ ...imageData })}>
          {cardContent}
        </CardActionArea>
      ) : (
        cardContent
      )}
    </Card>
  );
}

ImageCard.propTypes = {
  magicPreviewMode: PropTypes.bool,
  description: PropTypes.string,
  clickable: PropTypes.bool,
  imageData: PropTypes.shape(MagicSvgImageShape),
  materialImageUrl: PropTypes.string,
  materialNominalThickness: PropTypes.number,
  width: PropTypes.number,
  height: PropTypes.number,
  showShare: PropTypes.bool,
};

ImageCard.defaultProps = {
  magicPreviewMode: false,
  description: '',
  clickable: false,
  imageData: {
    id: '',
    svgUrl: '',
    imageUrl: '',
    prompt: '',
    artStyle: '',
    createdAt: '',
    userId: '',
    additionalFields: {},
  },
  materialImageUrl: '',
  materialNominalThickness: 0,
  width: 150,
  height: 150,
  showShare: false,
};
